import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  FileText, 
  Plus, 
  Trash2, 
  Eye, 
  Download,
  FileSpreadsheet,
  Truck,
  Mail,
  CheckCircle
} from "lucide-react";

const API = '/api';

export default function Encomendas() {
  const [encomendas, setEncomendas] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEncomendas = async () => {
    try {
      const response = await axios.get(`${API}/encomendas`);
      setEncomendas(response.data.reverse());
    } catch (error) {
      toast.error("Erro ao carregar encomendas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEncomendas();
  }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/encomendas/${id}`);
      toast.success("Encomenda eliminada");
      fetchEncomendas();
    } catch (error) {
      toast.error("Erro ao eliminar encomenda");
    }
  };

  const downloadPDF = async (id) => {
    try {
      const response = await axios.get(`${API}/encomendas/${id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `encomenda_${id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF gerado com sucesso");
    } catch (error) {
      toast.error("Erro ao gerar PDF");
    }
  };

  const downloadExcel = async (id) => {
    try {
      const response = await axios.get(`${API}/encomendas/${id}/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `encomenda_${id.slice(0, 8)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel gerado com sucesso");
    } catch (error) {
      toast.error("Erro ao gerar Excel");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="encomendas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-slate-900">
            Encomendas
          </h1>
          <p className="text-slate-500 mt-1">
            Lista de todas as encomendas
          </p>
        </div>
        <Link to="/nova-encomenda">
          <Button 
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            data-testid="new-order-btn"
          >
            <Plus className="w-4 h-4" />
            Nova Encomenda
          </Button>
        </Link>
      </div>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {encomendas.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="font-heading font-semibold text-lg text-slate-700 mb-2">
                Nenhuma encomenda registada
              </h3>
              <p className="text-slate-500 mb-4">
                Crie a sua primeira encomenda
              </p>
              <Link to="/nova-encomenda">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Encomenda
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80">
                    <TableHead className="font-bold text-slate-700">Farmácia</TableHead>
                    <TableHead className="font-bold text-slate-700">Armazenista</TableHead>
                    <TableHead className="font-bold text-slate-700">Período</TableHead>
                    <TableHead className="font-bold text-slate-700">Produtos</TableHead>
                    <TableHead className="font-bold text-slate-700 text-right">Total</TableHead>
                    <TableHead className="font-bold text-slate-700">Email</TableHead>
                    <TableHead className="font-bold text-slate-700 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {encomendas.map((encomenda) => (
                    <TableRow 
                      key={encomenda.id} 
                      className="table-row-hover"
                      data-testid={`order-row-${encomenda.id}`}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{encomenda.farmacia_nome}</p>
                          <p className="text-xs text-slate-500">
                            {encomenda.criado_por_nome && `Por: ${encomenda.criado_por_nome}`}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-blue-600" />
                          <span className="font-medium text-slate-700">{encomenda.armazenista}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {encomenda.mes} {encomenda.ano}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-600">
                          {encomenda.itens.filter(i => i.quantidade > 0).length} itens
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono font-semibold text-slate-900">
                          €{encomenda.total_geral.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {encomenda.email_enviado ? (
                          <span className="flex items-center gap-1 text-blue-600 text-sm">
                            <CheckCircle className="w-4 h-4" />
                            Enviado
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-slate-400 text-sm">
                            <Mail className="w-4 h-4" />
                            Pendente
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Link to={`/encomendas/${encomenda.id}`}>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-slate-400 hover:text-blue-600"
                              data-testid={`view-order-${encomenda.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => downloadPDF(encomenda.id)}
                            className="text-slate-400 hover:text-red-600"
                            data-testid={`download-pdf-${encomenda.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => downloadExcel(encomenda.id)}
                            className="text-slate-400 hover:text-green-600"
                            data-testid={`download-excel-${encomenda.id}`}
                          >
                            <FileSpreadsheet className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-slate-400 hover:text-red-500"
                                data-testid={`delete-order-${encomenda.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Eliminar Encomenda?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Esta ação não pode ser revertida. A encomenda será permanentemente eliminada.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(encomenda.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
